import { useEffect, useRef, useState } from "react"

import type { ReportId } from "../config/reports-data"
import {
  formatUnlockLabel,
  INCLUDED_UNLOCK_REPORT_IDS,
  isIncludedUnlockInstant,
  UNLOCK_DURATION_MS,
  unlockRemainingMs,
  useReportUnlockStore,
} from "./report-unlock-store"
import { useUserReports } from "./use-user-reports"

interface ReportUnlock {
  /** True while the unlock window is still running. */
  inProgress: boolean
  remainingMs: number
  /** Remaining time as "M:SS". */
  remainingLabel: string
}

function isIncludedUnlockReport(reportId: ReportId): boolean {
  return (INCLUDED_UNLOCK_REPORT_IDS as readonly ReportId[]).includes(reportId)
}

/**
 * Live "report in progress" countdown for a single report. Ticks once a second
 * and derives the remaining time from the stored purchase timestamp, so it is
 * correct however long the screen was away.
 *
 * Local timer always gates while remaining > 0 (OTP and included), even if the
 * API is already `ready`. Liked-review instant unlock permanently skips the
 * included timer. OTP timers missing after reload are re-seeded from `createdAt`.
 */
export function useReportUnlock(reportId: ReportId): ReportUnlock {
  const localStartedAt = useReportUnlockStore((s) => s.startedAt[reportId])
  const localDurationMs = useReportUnlockStore((s) => s.durationMs[reportId])
  const { byType, refetch } = useUserReports()
  const [now, setNow] = useState(() => Date.now())

  const api = byType(reportId)
  const serverProcessing = api?.status === "processing"
  const serverReady = api?.status === "ready"
  const estimateMs = api?.estimatedReadyAt ? Date.parse(api.estimatedReadyAt) : null
  const instantUnlocked =
    isIncludedUnlockReport(reportId) && isIncludedUnlockInstant(reportId)

  // OTP: after reload sessionStorage may be empty — rebuild the 10‑min window
  // from the purchase `createdAt` while it still has time left. Included reports
  // use subscription.startedAt sync instead; never seed those from createdAt.
  const createdAtMs = api?.createdAt ? Date.parse(api.createdAt) : NaN
  const otpSeedStartedAt =
    !instantUnlocked &&
    localStartedAt === undefined &&
    !isIncludedUnlockReport(reportId) &&
    (api?.status === "ready" || api?.status === "processing") &&
    Number.isFinite(createdAtMs) &&
    now < createdAtMs + UNLOCK_DURATION_MS
      ? createdAtMs
      : undefined
  const effectiveStartedAt = instantUnlocked ? undefined : (localStartedAt ?? otpSeedStartedAt)

  useEffect(() => {
    if (otpSeedStartedAt === undefined) return
    if (useReportUnlockStore.getState().startedAt[reportId] !== undefined) return
    useReportUnlockStore.getState().syncUnlock(reportId, otpSeedStartedAt, UNLOCK_DURATION_MS)
  }, [reportId, otpSeedStartedAt])

  const duration = localDurationMs ?? UNLOCK_DURATION_MS
  const localRemaining = unlockRemainingMs(effectiveStartedAt, now, duration)
  const localRunning = !instantUnlocked && localRemaining > 0
  const serverRemaining =
    serverProcessing && !serverReady ? Math.max(0, (estimateMs ?? now) - now) : 0
  const active = localRunning || (!instantUnlocked && serverProcessing && !serverReady)

  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [active])

  const remainingMs = localRunning
    ? localRemaining
    : serverProcessing && !serverReady
      ? serverRemaining
      : 0

  // Re-fetch once when the estimate elapses, so a report that finished generating
  // picks up its server `ready` url. Conservative: one extra call, no polling.
  const estimatePassed = serverProcessing && estimateMs !== null && now >= estimateMs
  const refetchedRef = useRef(false)
  useEffect(() => {
    if (!estimatePassed) {
      refetchedRef.current = false
      return
    }
    if (refetchedRef.current) return
    refetchedRef.current = true
    void refetch()
  }, [estimatePassed, refetch])

  return {
    inProgress: active,
    remainingMs,
    remainingLabel: formatUnlockLabel(remainingMs),
  }
}

/**
 * Shared once-a-second clock for a reports list. Ticks only while at least one
 * report's unlock window is still open and self-stops when the last one elapses,
 * so a parent list can re-sort at the exact moment a timer ends (dropping the
 * now-finished report to the bottom) without every row driving the re-sort.
 * Returns `now` (epoch ms); pair it with `unlockRemainingMs` to derive state.
 */
export function useUnlockClock(startedAt: Partial<Record<ReportId, number>>): number {
  const durationByReport = useReportUnlockStore((s) => s.durationMs)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const anyRunning = () =>
      (Object.entries(startedAt) as [ReportId, number | undefined][]).some(
        ([id, ts]) =>
          unlockRemainingMs(ts, Date.now(), durationByReport[id] ?? UNLOCK_DURATION_MS) > 0,
      )
    if (!anyRunning()) return
    const id = window.setInterval(() => {
      setNow(Date.now())
      if (!anyRunning()) window.clearInterval(id)
    }, 1000)
    return () => window.clearInterval(id)
  }, [startedAt, durationByReport])

  return now
}
